/* eslint-disable max-statements */
import { getConnectedIslands } from '.';
import { Island } from '../../islands/island.class';
import { nearestSixteenth } from '../../../helpers';
import { Kitchen } from '../kitchen.class';
import { KitchenSize } from '../types';
import { Size } from '../../../types';
import { Vertex3 } from '../../../spatial-classes';

/**
 * Computes bounding-box size by rounding each edge independently before summing.
 * Corner islands at 45° introduce irrational √2 coordinates — rounding each edge
 * first prevents those residuals from pushing the total to the wrong 1/16" boundary.
 */
const boundingSize = (minV: Vertex3, maxV: Vertex3): Size => ({
	width: nearestSixteenth(maxV.x) + Math.abs(nearestSixteenth(minV.x)),
	height: nearestSixteenth(maxV.y) + Math.abs(nearestSixteenth(minV.y)),
	depth: nearestSixteenth(maxV.z) + Math.abs(nearestSixteenth(minV.z))
});

export const getSizeCfn = (kitchen: Kitchen, includeCountertop = true): KitchenSize => {
	let hasExecuted = false;

	// center kitchen in viewspace
	const min = new Vertex3();
	const max = new Vertex3();
	const left = { min: new Vertex3(), max: new Vertex3() };
	const right = { min: new Vertex3(), max: new Vertex3() };

	// get default island center offsets
	const defaultIsland = kitchen.design.islands.getDefaultIsland();

	// initialize kitchen size
	const ks: KitchenSize = {
		left: {
			origin: new Vertex3(),
			size: { height: 0, width: 0, depth: 0 }
		},
		right: {
			origin: new Vertex3(),
			size: { height: 0, width: 0, depth: 0 }
		},
		origin: new Vertex3(),
		height: defaultIsland?.islandSize.height ?? 0,
		width: defaultIsland?.islandSize.width ?? 0,
		depth: defaultIsland?.islandSize.depth ?? 0
	};

	// something isn't right, return a zero sized kitchen
	if (!defaultIsland) return ks;

	let offsets = defaultIsland.getCenterOffsets(defaultIsland.axis.y, includeCountertop);
	// const halfHeight = defaultIsland.size.height / 2;

	// set default island left
	left.min.add(new Vertex3({ dataIn: { x: offsets.left, y: 0, z: offsets.front }}));
	left.max.add(new Vertex3({ dataIn: { y: defaultIsland.size.height, z: offsets.back }}));

	ks.left.size = boundingSize(left.min, left.max);

	// set default island right
	right.min.add(new Vertex3({ dataIn: { y: 0, z: offsets.front }}));
	right.max.add(new Vertex3({ dataIn: { x: offsets.right, y: defaultIsland.size.height, z: offsets.back }}));

	// get islands left of default island center
	const leftIslands: Island[] = [];
	defaultIsland.connections.children
		.filter((c) => !c.isSource && c.targetIsland?.origin?.x < 0)
		.forEach((c) => getConnectedIslands(c.targetIsland as Island, leftIslands));

	leftIslands.forEach((island) => {
		hasExecuted = true;

		const angle = island.axis.y;
		offsets = island.getCenterOffsets(angle, includeCountertop);
		const { height } = island.size;

		left.min.x = Math.min(left.min.x, island.origin.x + offsets.left);
		left.max.x = Math.max(left.max.x, island.origin.x + offsets.right);
		left.min.y = Math.min(left.min.y, 0);
		left.max.y = Math.max(left.max.y, height);
		left.min.z = Math.min(left.min.z, island.origin.z + offsets.front);
		left.max.z = Math.max(left.max.z, island.origin.z + offsets.back);
	});
	ks.left.size = boundingSize(left.min, left.max);
	ks.left.origin.x = (left.max.x + left.min.x) / 2;
	ks.left.origin.y = (left.max.y + left.min.y) / 2;
	ks.left.origin.z = (left.max.z + left.min.z) / 2;

	// get islands right of default island center
	const rightIslands: Island[] = [];
	defaultIsland.connections.children
		.filter((c) => !c.isSource && c.targetIsland?.origin?.x > 0)
		.forEach((c) => getConnectedIslands(c.targetIsland as Island, rightIslands));

	rightIslands.forEach((island) => {
		hasExecuted = true;

		const angle = island.axis.y;
		offsets = island.getCenterOffsets(angle, includeCountertop);
		const { height } = island.size;

		right.min.x = Math.min(right.min.x, island.origin.x + offsets.left);
		right.max.x = Math.max(right.max.x, island.origin.x + offsets.right);
		right.min.y = Math.min(right.min.y, 0);
		right.max.y = Math.max(right.max.y, height);
		right.min.z = Math.min(right.min.z, island.origin.z + offsets.front);
		right.max.z = Math.max(right.max.z, island.origin.z + offsets.back);
	});
	ks.right.size = boundingSize(right.min, right.max);
	ks.right.origin.x = (right.max.x + right.min.x) / 2;
	ks.right.origin.y = (right.max.y + right.min.y) / 2;
	ks.right.origin.z = (right.max.z + right.min.z) / 2;

	// calculate the overall kitchen size and origin
	// dont calculate or log when island loop wasn't run
	if (hasExecuted) {
		min.x = Math.min(left.min.x, right.min.x);
		max.x = Math.max(left.max.x, right.max.x);
		min.y = Math.min(left.min.y, right.min.y);
		max.y = Math.max(left.max.y, right.max.y);
		min.z = Math.min(left.min.z, right.min.z);
		max.z = Math.max(left.max.z, right.max.z);

		const overall = boundingSize(min, max);
		ks.width = overall.width;
		ks.height = overall.height;
		ks.depth = overall.depth;

		ks.origin.x = (max.x + min.x) / 2;
		ks.origin.y = (max.y + min.y) / 2;
		ks.origin.z = (max.z + min.z) / 2;
	}

	// console.log('getSizeCfn', { ks });
	return ks;
};
